import React, { useEffect, useState, useRef, useCallback } from 'react';
import Axios from '../../api/axios';
import { getCached, setCached, clearCached, casRefCacheKey } from '../../utils/requestCache.js';
import { resolveCeTutarFromCeTur } from '../../utils/ekimKanalFiyat.js';
import { asArray } from '../../utils/arrayData.js';
import DataGrid, { Paging, Column, LoadPanel, Scrolling, Lookup } from '../BootstrapDataGrid';
import { Button } from '../BootstrapControls';
import Form, { GroupItem, SimpleItem, Label } from '../BootstrapForm';
import notify from '../../utils/notifyUtil';
import Dict from '../../messages.js';
import * as glb from '../../globals.js';
import { emitCariSync } from '../../utils/cariSyncBus.js';
import { addMaxLengthToGridColumn } from '../../utils/formValidationHelper.js';
import { attachFormEnterToNextField } from '../../utils/formEnterFocusNext';
import { canDeleteByOwner, ekimIsYapanRef } from '../../utils/ownRecordDelete.js';
import {
  filterEkimKanalKullanicilar,
  filterEkimKanalKullanicilarForGrid,
  getEkimKanalCeTurItems,
  isCeTurSelected,
} from '../../utils/ekimKanalKullanici.js';
import { gridHeightByRowCount, gridMaxHeightForRows } from '../../utils/gridAutoHeight.js';

/**
 * EkimKanalAlimPanel: İşlemPanel benzeri, sadece batch modda çalışan basit grid.
 * - Amaç: Kanal/Ekim alım randevularını listelemek ve batch modda güncellemek.
 * - Özellikler: CI_ISL_TAR işaretleme ile ileri randevuları otomatik öteleyebilir.
 *
 * Props:
 *  - carRef: aktif CAR_REF
 *  - casRef: aktif CAS_REF (satış/işlem üst kaydı)
 *  - readOnly: (optional) true ise düzenleme kapalı
 */
export default function EkimKanalAlimPanel({ carRef, casRef, readOnly, gridVisibleRowCount = null, gridHeight = null }) {
  const [rows, setRows] = useState([]);
  const [activeRow, setActiveRow] = useState(null);
  const [formReadOnly, setFormReadOnly] = useState(true);
  const [formDirty, setFormDirty] = useState(false);
  const [newFormToken, setNewFormToken] = useState(0);
  const [focusedEkimKey, setFocusedEkimKey] = useState(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const createLockRef = useRef(false);
  const gridRef = useRef(null);
  const ceTurPriceReqSeqRef = useRef(0);
  const enterNavCleanupRef = useRef(null);
  const attachEnterNavRoot = useCallback((node) => {
    if (enterNavCleanupRef.current) {
      enterNavCleanupRef.current();
      enterNavCleanupRef.current = null;
    }
    if (node) {
      enterNavCleanupRef.current = attachFormEnterToNextField(node);
    }
  }, []);
  useEffect(() => () => {
    if (enterNavCleanupRef.current) {
      enterNavCleanupRef.current();
      enterNavCleanupRef.current = null;
    }
  }, []);
  const isNewUnsavedRecord = !casRef || casRef === -1 || casRef <= 0;
  const isFormRecordNew = !activeRow || Number(activeRow.CE_REF) <= 0;
  const allKullanicilar = React.useMemo(
    () => asArray(glb.default.KULLANICILkp),
    []
  );
  const sabitLkp = glb.default.sabitLkp;
  const allYapanKullanicilar = React.useMemo(
    () => filterEkimKanalKullanicilarForGrid(allKullanicilar),
    [allKullanicilar]
  );
  const yapanKullanicilar = React.useMemo(
    () => filterEkimKanalKullanicilar(allKullanicilar, activeRow?.CE_TUR, sabitLkp),
    [allKullanicilar, activeRow?.CE_TUR, sabitLkp]
  );
  const ceTurSecili = isCeTurSelected(activeRow?.CE_TUR);
  const resolveDefaultYapanRef = useCallback((ceTur = null) => {
    const list = filterEkimKanalKullanicilar(allKullanicilar, ceTur, sabitLkp);
    const currentKulRef = Number(glb.default?.KULLANICI?.KUL_REF || 0);
    if (currentKulRef && list.some((k) => Number(k.KUL_REF) === currentKulRef)) {
      return currentKulRef;
    }
    if (list.length > 0) return Number(list[0].KUL_REF);
    return null;
  }, [allKullanicilar, sabitLkp]);
  const islemTurList = React.useMemo(() => getEkimKanalCeTurItems(), []);
  const displayRows = React.useMemo(() => {
    if (isCreatingNew && activeRow && isFormRecordNew) return [...(rows || []), activeRow];
    return rows || [];
  }, [rows, isCreatingNew, activeRow, isFormRecordNew]);

  const fetchRows = useCallback((forceReload = false) => {
    if (!casRef || typeof casRef !== 'number' || casRef <= 0) { setRows([]); return; }
    const cacheKey = casRefCacheKey('EKIMKANALALIM_LIST', casRef);
    if (forceReload) {
      clearCached(cacheKey);
    }
    const cached = forceReload ? null : getCached(cacheKey);
    if (cached && Array.isArray(cached)) {
      setRows(cached);
      return;
    }
    Axios.get(`${process.env.REACT_APP_BACKEND_SERVER}CARI_EKIMKANALALIM/list?CAS_REF=${casRef}`)
      .then(res => {
        const data = res.data || [];
        setCached(cacheKey, data);
        setRows(data);
      })
      .catch(err => { console.log(err); notify(Dict.get('serverError') + err, 'error', 2000); });
  }, [casRef]);

  const resolveAutoCeTutar = useCallback(async (ceTur) => {
    const ceTurNum = Number(ceTur);
    if (!Number.isFinite(ceTurNum)) return null;

    const localTutar = resolveCeTutarFromCeTur(ceTurNum, glb.default.sabitLkp);
    if (localTutar != null) return localTutar;

    const requestSeq = ++ceTurPriceReqSeqRef.current;
    try {
      const res = await Axios.get(`${process.env.REACT_APP_BACKEND_SERVER}CARI_EKIMKANALALIM/price?CE_TUR=${ceTurNum}`);
      if (requestSeq !== ceTurPriceReqSeqRef.current) return null;
      const tutar = Number(res?.data?.CE_TUTAR);
      return Number.isFinite(tutar) ? tutar : null;
    } catch (err) {
      console.log(err);
      return null;
    }
  }, []);

  const applyCeTurSelection = useCallback(async (ceTur, ceYapKul = null) => {
    if (!isCeTurSelected(ceTur)) {
      setActiveRow((prev) => ({
        ...(prev || {}),
        CE_TUR: null,
        CE_YAP_KUL: null,
        CE_TUTAR: null,
      }));
      setFormDirty(true);
      return;
    }
    const ceTurNum = Number(ceTur);
    const otomatikTutar = await resolveAutoCeTutar(ceTurNum);
    const list = filterEkimKanalKullanicilar(allKullanicilar, ceTurNum, sabitLkp);
    setActiveRow((prev) => {
      const next = { ...(prev || {}), CE_TUR: ceTurNum };
      if (otomatikTutar != null) next.CE_TUTAR = otomatikTutar;
      const curYapan = Number(ceYapKul ?? next.CE_YAP_KUL ?? 0);
      if (!curYapan || !list.some((k) => Number(k.KUL_REF) === curYapan)) {
        const def = resolveDefaultYapanRef(ceTurNum);
        next.CE_YAP_KUL = def != null ? def : null;
      }
      return next;
    });
    setFormDirty(true);
  }, [resolveAutoCeTutar, allKullanicilar, sabitLkp, resolveDefaultYapanRef]);

  useEffect(() => { setFormReadOnly(true); setFormDirty(false); setIsCreatingNew(false); createLockRef.current = false; }, [casRef]);
  useEffect(() => { fetchRows(); }, [fetchRows]);
  useEffect(() => {
    if (!formReadOnly || isCreatingNew || createLockRef.current) return;
    if (!Array.isArray(rows) || rows.length === 0) { setActiveRow(null); setFocusedEkimKey(null); return; }
    if (!activeRow || !rows.some(x => Number(x.CE_REF) === Number(activeRow.CE_REF))) {
      setActiveRow(rows[0]);
      setFocusedEkimKey(rows[0]?.CE_REF ?? null);
      setFormReadOnly(true);
      setFormDirty(false);
    }
  }, [rows, activeRow, formReadOnly, isCreatingNew]);

  const saveChanges = async ({ inserted = [], updated = [], deleted = [] }) => {
    try {
      const normalizeRow = (r) => {
        if (!r || typeof r !== 'object') return r;
        const hasCeSil = Object.prototype.hasOwnProperty.call(r, 'CE_SIL');
        if (!hasCeSil) return r;
        const v = r.CE_SIL;
        if (typeof v === 'boolean') return r;
        if (v === 1 || v === '1' || v === 'true') return { ...r, CE_SIL: true };
        if (v === 0 || v === '0' || v === 'false' || v === null || v === undefined) return { ...r, CE_SIL: false };
        return { ...r, CE_SIL: Boolean(v) };
      };

      const firRef = glb.default?.KULLANICI?.KUL_DEF_FIR_REF ?? null;
      // Enrich inserts with parent refs
      const ins = inserted.map(r => ({
        ...normalizeRow(r),
        CE_CAR_REF: carRef,
        CE_CAS_REF: casRef,
        CE_FIR_REF: firRef,
      }));
      const upd = (updated || []).map(r => normalizeRow(r));
      const res = await Axios.post(`${process.env.REACT_APP_BACKEND_SERVER}CARI_EKIMKANALALIM/batch`, {
        inserted: ins,
        updated: upd,
        deleted,
        casRef,
        carRef,
        firRef,
        kulRef: glb.default.KULLANICI.KUL_REF,
      });
      if (res.data && res.data.success) {
        if (casRef) clearCached(casRefCacheKey('EKIMKANALALIM_LIST', casRef));
        notify(Dict.get('Changes saved successfully'), 'success', 1500);
        fetchRows();
        emitCariSync({ carRef, source: 'ekim' });
        return true;
      }
      notify(Dict.get('Error saving changes'), 'error', 2000);
      return false;
    } catch (err) {
      console.log(err);
      notify(Dict.get('serverError') + err, 'error', 2000);
      return false;
    }
  };

  const resolvedGridHeight = React.useMemo(() => {
    const maxFromProp = Number(gridHeight) > 0
      ? Number(gridHeight)
      : (Number(gridVisibleRowCount) > 0
        ? gridMaxHeightForRows(gridVisibleRowCount)
        : 500);
    return gridHeightByRowCount((displayRows || []).length, maxFromProp);
  }, [gridHeight, gridVisibleRowCount, displayRows]);

  return (
    <div ref={attachEnterNavRoot} style={{ width: '100%' }}>
        <div style={{ 
                        marginBottom: '8px', 
                        fontWeight: 'bold', 
                        fontSize: '14px',
                        color: 'var(--dx-color-text, #212529)',
                        borderBottom: '2px solid var(--dx-color-primary, #1976d2)',
                        paddingBottom: '4px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 8
                    }}>
                        <span>🔧 {Dict.get('Ekimci-Kanalcı-Alımcı')}</span>
                        {!isNewUnsavedRecord && (
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                {formReadOnly && (
                                    <>
                                        <Button text={Dict.get('Yeni Kayıt')} icon="plus" type="default" onClick={() => {
                                          createLockRef.current = true;
                                          setFocusedEkimKey(null);
                                          setNewFormToken(v => v + 1);
                                          setActiveRow({
                                            CE_REF: -1,
                                            CE_TUR: null,
                                            CE_ISL_TAR: new Date(),
                                            CE_TUTAR: null,
                                            CE_ACK: '',
                                            CE_YAP_KUL: null
                                          });
                                          setFormReadOnly(false);
                                          setFormDirty(false);
                                          setIsCreatingNew(true);
                                        }} disabled={readOnly} />
                                        <Button text={Dict.get('Düzenle')} icon="edit" type="default" onClick={() => { createLockRef.current = false; setFormReadOnly(false); setIsCreatingNew(false); }} disabled={readOnly || !activeRow || (Number(activeRow.CE_REF) > 0 && !canDeleteByOwner(glb.default?.KULLANICI, ekimIsYapanRef(activeRow)))} />
                                        {activeRow && Number(activeRow.CE_REF) > 0
                                          && canDeleteByOwner(glb.default?.KULLANICI, ekimIsYapanRef(activeRow)) && (
                                        <Button text={Dict.get('Sil')} icon="trash" type="danger" onClick={async () => {
                                          if (!window.confirm('Bu kayıt silinsin mi?')) return;
                                          const ok = await saveChanges({ inserted: [], updated: [], deleted: [activeRow.CE_REF] });
                                          if (ok) fetchRows();
                                        }} disabled={readOnly} />
                                        )}
                                        <Button text={Dict.get('Yenile')} icon="refresh" type="default" onClick={() => { fetchRows(true); setFormReadOnly(true); setFormDirty(false); }} />
                                    </>
                                )}
                                {!formReadOnly && (
                                    <>
                                        <Button text={Dict.get('Kaydet')} icon="save" type="success" onClick={async () => {
                                          if (!activeRow) return;
                                          const isNew = Number(activeRow.CE_REF) <= 0;
                                          const ok = await saveChanges({
                                            inserted: isNew ? [activeRow] : [],
                                            updated: isNew ? [] : [activeRow],
                                            deleted: []
                                          });
                                          if (ok) { createLockRef.current = false; setFormReadOnly(true); setFormDirty(false); setIsCreatingNew(false); fetchRows(); }
                                        }} disabled={!isFormRecordNew && !formDirty} />
                                        <Button text={Dict.get('Vazgeç')} icon="undo" type="warning" onClick={() => { createLockRef.current = false; fetchRows(); setFormReadOnly(true); setFormDirty(false); setIsCreatingNew(false); }} />
                                    </>
                                )}
                            </div>
                        )}
                    </div>
      {isNewUnsavedRecord && (
        <div style={{ padding: 8, margin: '8px 0', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 4, color: '#856404' }}>
          ℹ️ Kayıt eklemek için önce satış kaydını kaydedin.
        </div>
      )}
      {activeRow && (
        <Form
          key={`ekim-form-${activeRow?.CE_REF ?? 'new'}-${newFormToken}-${activeRow?.CE_TUR ?? 'x'}`}
          formData={activeRow}
          readOnly={formReadOnly || readOnly}
          colCount={1}
          onFieldDataChanged={(e) => {
            setActiveRow(prev => ({ ...prev, [e.dataField]: e.value }));
            setFormDirty(true);
          }}
        >
          <GroupItem colCount={2}>
            <SimpleItem dataField={'CE_TUR'} editorType="dxSelectBox" editorOptions={{
              items: islemTurList,
              searchEnabled: true,
              searchMode: 'contains',
              displayExpr: 'ST_AD',
              searchExpr: 'ST_AD',
              valueExpr: 'ST_KODINT',
              onValueChanged: async (e) => {
                if (!e?.event) return;
                await applyCeTurSelection(e?.value);
              }
            }}><Label text={Dict.get('İşlem Türü')} /></SimpleItem>
            <SimpleItem dataField={'CE_ISL_TAR'} editorType="dxDateBox"><Label text={Dict.get('İşlem Tarihi')} /></SimpleItem>
            <SimpleItem dataField={'CE_YAP_KUL'} editorType="dxSelectBox" editorOptions={{
              items: yapanKullanicilar,
              searchEnabled: true,
              searchMode: 'contains',
              displayExpr: 'KUL_ADI',
              searchExpr: 'KUL_ADI',
              valueExpr: 'KUL_REF',
              showClearButton: true,
              disabled: !ceTurSecili,
            }}><Label text={Dict.get('Yapan')} /></SimpleItem>
            <SimpleItem dataField={'CE_TUTAR'} editorType="dxNumberBox" editorOptions={{ format: '#,##0.00' }}><Label text={Dict.get('Tutar')} /></SimpleItem>
          </GroupItem>
          <SimpleItem dataField={'CE_ACK'} editorType="dxTextBox"><Label text={Dict.get('Açıklama')} /></SimpleItem>
        </Form>
      )}

      {!isNewUnsavedRecord && (
        <DataGrid
          key={`ekim-kanal-grid-${casRef}-${rows.length}`}
          dataSource={displayRows}
          showBorders={true}
          keyExpr={'CE_REF'}
          focusedRowEnabled={!isCreatingNew}
          focusedRowKey={isCreatingNew ? null : focusedEkimKey}
          hoverStateEnabled={false}
          repaintChangesOnly={false}
          ref={gridRef}
          height={resolvedGridHeight}
          width={'100%'}
          columnAutoWidth={false}
          columnMinWidth={120}
          allowColumnResizing={true}
          onFocusedRowChanged={(e) => {
            if (!formReadOnly || createLockRef.current) return;
            const selected = e.row?.data || null;
            if (selected) {
              createLockRef.current = false;
              setActiveRow(selected);
              setFocusedEkimKey(selected.CE_REF);
              setIsCreatingNew(false);
            }
          }}
          toolbar={{ visible: false }}
        >
          <Paging enabled={false} />
          <Scrolling mode="standard" useNative={false} showScrollbar="always" columnRenderingMode="standard" />
          <LoadPanel enabled={true} />

          {/* Kolonlar: gecici_ekimciPanel.js baz alınarak (yoksa varsayılanlar) */}
          <Column dataField={'CE_REF'} visible={false} caption={Dict.get('K.No')} allowEditing={false} />
          <Column dataField={'CE_TUR'} caption={Dict.get('İşlem Türü')}>
            <Lookup dataSource={islemTurList} searchEnabled={true} searchMode={'contains'} displayExpr={'ST_AD'} searchExpr={'ST_AD'} valueExpr={'ST_KODINT'} />
          </Column>
          <Column dataField={'CE_ISL_TAR'} dataType={'date'} format={'dd-MM-yyyy'} caption={Dict.get('İşlem Tarihi')} />
          <Column dataField={'CE_YAP_KUL'} caption={Dict.get('Yapan')}>
            <Lookup dataSource={allYapanKullanicilar} searchEnabled={true} searchMode={'contains'} displayExpr={'KUL_ADI'} searchExpr={'KUL_ADI'} valueExpr={'KUL_REF'} />
          </Column>
          <Column dataField={'CE_TUTAR'} dataType={'number'} format={'#,##0.##'} caption={Dict.get('Tutar')} />
          <Column {...addMaxLengthToGridColumn('CARI_EKIMKANALALIM', 'CE_ACK', {
            dataField: 'CE_ACK', 
            caption: Dict.get('Açıklama')
          })} />
        </DataGrid>
      )}
    </div>
  );
}
